/**
 * Keeps the site-wide sticky free-delivery bar in sync with the cart.
 *
 * The bar lives in the header group, OUTSIDE any cart section, so the theme's
 * own cart update does not re-render it. Rather than recompute the remaining
 * amount in JS - which would duplicate the Liquid maths and the money format,
 * and could drift from what the cart actually says - this re-fetches the
 * rendered section from Shopify and swaps the markup in. Liquid stays the
 * single source of truth.
 */
(() => {
  const SELECTOR = '[data-bedjoy-ship-bar]';
  const EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'shopify:cart:note-update'];

  let inFlight = null;

  /**
   * The theme dispatches its cart events before the server-side cart is
   * guaranteed to be settled, so a single immediate fetch can render the
   * PREVIOUS cart state and write it back - leaving the bar stale (or missing)
   * until the next navigation. Re-running after a longer delay is idempotent
   * and self-corrects whichever way the race lands.
   */
  const scheduleRefresh = () => {
    setTimeout(refresh, 250);
    setTimeout(refresh, 1200);
  };

  const refresh = async () => {
    const current = document.querySelector(SELECTOR);
    if (!current) return;

    const sectionId = current.dataset.sectionId;
    if (!sectionId) return;

    // Collapse concurrent updates (e.g. rapid quantity clicks) into one request.
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}?sections=${encodeURIComponent(sectionId)}`);
        if (!response.ok) return;

        const data = await response.json();
        const html = data[sectionId];
        if (typeof html !== 'string') return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector(SELECTOR);
        const target = document.querySelector(SELECTOR);
        if (!target) return;

        if (next) {
          target.replaceWith(next);
        } else {
          // Cart emptied - the section renders nothing inside the wrapper.
          target.innerHTML = '';
        }
      } catch {
        // Never let a failed refresh break the page; the bar simply goes stale
        // until the next navigation, which re-renders it server-side anyway.
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };

  for (const name of EVENTS) {
    document.addEventListener(name, scheduleRefresh);
  }
})();
